import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@vibe/core';
import AssessmentStatusReveal from '@/components/assessment/AssessmentStatusReveal';
import Doch33ReportView from '@/components/planning/Doch33ReportView';
import PlanningDocumentsBlock from '@/components/planning/PlanningDocumentsBlock';
import PlanningReportSkeleton from '@/components/planning/PlanningReportSkeleton';
import JourneyProgressStepper from '@/components/wizard/JourneyProgressStepper';
import { AppBanner, Card, JourneyScreenBack } from '@/components/ui';
import PageShell from '@/components/ui/PageShell';
import {
  clearWizardSession,
  isIntakeSubmitted,
  useWizardSession,
} from '@/context/WizardSessionContext';
import { clearAssessmentPlanningCache } from '@/lib/assessment-planning-cache';
import { useTranslation } from '@/i18n/useTranslation';
import { fetchAssessment, type AssessmentResponse } from '@/lib/api/assessment';
import { ApiError } from '@/lib/api/client';
import { answersToApi } from '@/lib/api/session-mapper';
import { patchSessionSteps } from '@/lib/api/sessions';
import { JOURNEY_STEP, JOURNEY_UI_STEP } from '@/lib/journey-steps';
import { trackEvent } from '@/lib/analytics';
import {
  parsePlanningExtras,
  hasTakanonDocument,
  hasVerifiedTakanonExtraction,
} from '@/lib/api/planning-info-mapper';
import { isRtlLocale } from '@/lib/planning-report-i18n';
import {
  exportPlanningReportPdf,
  openPlanningReportPrintPreview,
} from '@/lib/planning-report-html-pdf';
import { useLocale } from '@/i18n/LocaleProvider';
import {
  isAssessmentPlanningCacheComplete,
  mergeAssessmentPlanningCache,
  peekAssessmentPlanningCache,
} from '@/lib/assessment-planning-cache';
import { usePlanningInfoReport } from '@/screens/PlanningInfo/usePlanningInfoReport';
import planningStyles from '@/screens/PlanningInfo/PlanningInfoScreen.module.css';
import styles from './AssessmentScreen.module.css';

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M10 3v9m0 0l3.5-3.5M10 12 6.5 8.5M4 14v1.5A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5V14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function AssessmentScreen() {
  const t = useTranslation();
  const { language } = useLocale();
  const navigate = useNavigate();
  const { sessionId, answers } = useWizardSession();
  const cachedBundle = sessionId ? peekAssessmentPlanningCache(sessionId) : null;
  const restoredFromCache = sessionId ? isAssessmentPlanningCacheComplete(sessionId) : false;

  const [assessment, setAssessment] = useState<AssessmentResponse | null>(
    cachedBundle?.assessment ?? null,
  );
  const [assessmentLoading, setAssessmentLoading] = useState(
    Boolean(sessionId) && !cachedBundle?.assessment,
  );
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [reportUnlocked, setReportUnlocked] = useState(restoredFromCache);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState<string | null>(null);

  const {
    planningInfo,
    view,
    loading: planningLoading,
    error: planningError,
    documentRef,
    parcelLine,
    labels,
  } = usePlanningInfoReport(sessionId ?? undefined);

  const assessmentLabels = {
    status: t.assessment.status,
    trafficLight: t.assessment.trafficLight,
    unknownNote: t.assessment.unknownNote,
  };

  const intakeSubmitted = isIntakeSubmitted();

  const handleFinishJourney = useCallback(() => {
    clearWizardSession();
    clearAssessmentPlanningCache();
    navigate('/', { replace: true, state: { intakeSubmitSuccess: true } });
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (pdfDownloadUrl) URL.revokeObjectURL(pdfDownloadUrl);
    };
  }, [pdfDownloadUrl]);

  useEffect(() => {
    if (!sessionId) return;
    void patchSessionSteps(sessionId, {
      current_step: JOURNEY_STEP.ASSESSMENT,
      answers: answersToApi(answers),
    }).catch(() => {
      /* best-effort journey sync */
    });
  }, [sessionId, answers]);

  useEffect(() => {
    if (!sessionId) return;

    const cached = peekAssessmentPlanningCache(sessionId)?.assessment;
    if (cached) {
      setAssessment(cached);
      setAssessmentLoading(false);
      setAssessmentError(null);
      return;
    }

    let cancelled = false;
    setAssessmentLoading(true);
    setAssessmentError(null);
    if (!restoredFromCache) {
      setReportUnlocked(false);
    }

    void fetchAssessment(sessionId)
      .then((data) => {
        if (cancelled) return;
        setAssessment(data);
        mergeAssessmentPlanningCache(sessionId, { assessment: data });
        trackEvent('assessment_viewed', { status: data.status, traffic_light: data.traffic_light });
      })
      .catch((err) => {
        if (cancelled) return;
        setAssessmentError(err instanceof Error ? err.message : t.assessment.errorTitle);
      })
      .finally(() => {
        if (!cancelled) setAssessmentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, restoredFromCache, t.assessment.errorTitle]);

  useEffect(() => {
    if (restoredFromCache) {
      setReportUnlocked(true);
      return;
    }
    if (assessmentLoading || assessmentError || !assessment) {
      setReportUnlocked(false);
      return;
    }
    const timer = window.setTimeout(() => setReportUnlocked(true), 520);
    return () => window.clearTimeout(timer);
  }, [assessment, assessmentError, assessmentLoading, restoredFromCache]);

  useEffect(() => {
    if (!planningInfo || restoredFromCache) return;
    trackEvent('planning_info_opened', { fetch_status: planningInfo.fetch_status });
  }, [planningInfo, restoredFromCache]);

  const extras = planningInfo
    ? parsePlanningExtras(planningInfo.sections_json as Record<string, unknown>)
    : { documents: [], extractedSections: [], takanonExtraction: undefined };
  const hasTakanon = hasTakanonDocument(extras);
  const showTakanonNotFoundBanner = reportUnlocked && !planningLoading && Boolean(planningInfo) && !hasTakanon;
  const showExtractionBanner =
    reportUnlocked &&
    !planningLoading &&
    Boolean(planningInfo) &&
    hasTakanon &&
    (extras.takanonExtraction?.status === 'failed' ||
      (planningInfo?.fetch_status === 'partial' && !hasVerifiedTakanonExtraction(extras)));

  const fetchStatus = planningInfo?.fetch_status;
  const showReport = reportUnlocked && !planningLoading && Boolean(planningInfo) && Boolean(view);
  const continueEnabled = showReport && !assessmentError;
  const statusHint =
    fetchStatus === 'partial'
      ? t.planningInfo.fetchStatus.partial
      : fetchStatus === 'failed'
        ? t.planningInfo.fetchStatus.failed
        : null;

  const handleExportPdf = useCallback(async () => {
    if (!planningInfo || !sessionId) return;

    setExportingPdf(true);
    setExportError(null);
    setPdfDownloadUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    const safeMigrash = (planningInfo.migrash ?? 'parcel').replace(/\s+/g, '-');
    const filename = `clicks-tourism-360-planning-${safeMigrash}.pdf`;

    try {
      const blob = await exportPlanningReportPdf({
        filename,
        sessionId,
        lang: language,
      });
      setPdfDownloadUrl(URL.createObjectURL(blob));
      trackEvent('planning_report_pdf_exported', {
        migrash: planningInfo.migrash,
        source: planningInfo.source,
        method: 'playwright-url',
      });
    } catch (err) {
      try {
        openPlanningReportPrintPreview(sessionId, language, { autoprint: true });
        setExportError(t.planningInfo.exportPrintFallback);
      } catch {
        const detail = err instanceof ApiError ? err.message : '';
        setExportError(
          detail ? `${t.planningInfo.exportPdfError} (${detail})` : t.planningInfo.exportPdfError,
        );
      }
    } finally {
      setExportingPdf(false);
    }
  }, [planningInfo, sessionId, language, t.planningInfo]);

  const reportSectionClass = useMemo(
    () =>
      [styles.reportSection, reportUnlocked ? styles.reportVisible : styles.reportHidden]
        .filter(Boolean)
        .join(' '),
    [reportUnlocked],
  );

  return (
    <PageShell
      width="wide"
      header={<JourneyScreenBack />}
      progress={<JourneyProgressStepper activeIndex={JOURNEY_UI_STEP.ASSESSMENT} />}
    >
      <Card
        className={`${styles.screen} ${planningStyles.screen}`.trim()}
        data-testid="assessment-screen"
      >
        <header className={styles.header}>
          <div className={styles.headerMain}>
            <h1 className={styles.title}>{t.assessment.combinedTitle}</h1>
            <p className={styles.subtitle}>{t.assessment.combinedSubtitle}</p>
            {statusHint && reportUnlocked ? (
              <p className={planningStyles.statusHint} data-testid="planning-info-fetch-status">
                {statusHint}
              </p>
            ) : null}
          </div>

          {intakeSubmitted ? (
            <div className={styles.headerCta}>
              <Button
                kind={Button.kinds.PRIMARY}
                size={Button.sizes.MEDIUM}
                onClick={handleFinishJourney}
                disabled={!continueEnabled}
                aria-disabled={!continueEnabled}
                data-testid="assessment-finish-journey"
              >
                {t.assessment.finishJourney}
              </Button>
              <p className={styles.ctaHint} aria-live="polite">
                {continueEnabled
                  ? t.assessment.finishJourneyHint
                  : t.assessment.ctaWaitingForReport}
              </p>
            </div>
          ) : null}
        </header>

        <AssessmentStatusReveal
          loading={assessmentLoading}
          assessment={assessment}
          error={assessmentError}
          errorTitle={t.assessment.errorTitle}
          loadingLabel={t.assessment.statusRevealLoading}
          loadingHint={t.assessment.statusRevealHint}
          labels={assessmentLabels}
        />

        <section className={reportSectionClass} aria-hidden={!reportUnlocked}>
          <div className={planningStyles.hero}>
            <h2 className={planningStyles.title}>{t.planningInfo.title}</h2>
            {showReport ? (
              <button
                type="button"
                className={planningStyles.exportButton}
                onClick={() => void handleExportPdf()}
                disabled={exportingPdf}
                data-testid="planning-export-pdf"
              >
                {exportingPdf ? t.planningInfo.exportingPdf : t.planningInfo.exportPdf}
                <DownloadIcon className={planningStyles.exportIcon} />
              </button>
            ) : null}
          </div>

          {planningError ? (
            <AppBanner
              variant="danger"
              title={t.planningInfo.errorTitle}
              text={planningError}
              data-testid="planning-info-error"
            />
          ) : null}

          {!showReport && reportUnlocked && !planningLoading ? (
            <AppBanner
              variant="info"
              title={t.planningInfo.disclaimerTitle}
              text={t.planningInfo.disclaimerBody}
              data-testid="planning-info-disclaimer"
            />
          ) : null}

          {showTakanonNotFoundBanner ? (
            <AppBanner
              variant="warning"
              compact
              title={t.planningInfo.takanonNotFoundTitle}
              text={t.planningInfo.takanonNotFoundBody}
              data-testid="planning-takanon-not-found"
            />
          ) : null}

          {showExtractionBanner ? (
            <AppBanner
              variant="warning"
              compact
              title={t.planningInfo.extractionBannerTitle}
              text={t.planningInfo.extractionBannerBody}
              data-testid="planning-extraction-banner"
            />
          ) : null}

          {exportError ? (
            <AppBanner variant="warning" text={exportError} compact role="alert" />
          ) : null}

          {pdfDownloadUrl ? (
            <p className={planningStyles.pdfReady}>
              <a
                href={pdfDownloadUrl}
                download={`clicks-tourism-360-planning-${(planningInfo?.migrash ?? 'parcel').replace(/\s+/g, '-')}.pdf`}
              >
                {t.planningInfo.exportPdf}
              </a>
            </p>
          ) : null}

          {reportUnlocked && planningLoading ? (
            <PlanningReportSkeleton label={labels.reportLoading} />
          ) : null}

          {showReport ? (
            <div className={planningStyles.reportStack} data-testid="planning-info-screen">
              {hasTakanon && extras.documents.length > 0 ? (
                <PlanningDocumentsBlock
                  documents={extras.documents}
                  title={t.planningInfo.documentsTitle}
                />
              ) : null}

              <Doch33ReportView
                variant="screen"
                view={view!}
                dir={isRtlLocale(language) ? 'rtl' : 'ltr'}
                reportHeading={labels.title}
                reportMeta={view!.reportMeta}
                reportDateLabel={labels.reportDate}
                requestTypeLabel={labels.requestType}
                parcelLine={parcelLine}
                documentRefLabel={labels.reportDocumentRef}
                documentRef={documentRef}
                informationalNotice={labels.informationalNotice}
                verifySourceLabel={labels.verifySourceLabel}
                openPdfOnPageLabel={labels.openPdfOnPageLabel}
                extractionDraftLabel={labels.extractionDraftLabel}
              />
            </div>
          ) : null}
        </section>
      </Card>
    </PageShell>
  );
}
